<template>
  <div class="p-documents-tab py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
          <p-search-input
            v-model="search"
            :items="documentNames"
        ></p-search-input>
        </div>
        <div v-if="hasAccess()" class="p-toolbar-item p-right">
          <v-btn
            class="p-btn-rounded ml-4 mr-1"
            large
            rounded
            @click.stop="openAddDialog"
          >
            {{ $t('document.createDocument') }}
          </v-btn>
        </div>
      </div>
      <v-row>
        <v-col v-if="documents.length > 0">
          <v-data-table
            :headers="tableHeaders"
            :items="documentsComputed"
            :search="search"
            :page.sync="page"
            :items-per-page="itemsPerPage"
            class="p-no-borders"
            hide-default-footer
            @pagination="setPagination"
          >
            <template v-slot:[`item.name`]="{ item }">
              <p-file-icon :extension="item.extension"></p-file-icon>
              <span class="p-link" @click="downloadFile(item)">{{item.name}}</span>
            </template>
            <template v-slot:[`item.changed`]="{ item }">
              <v-tooltip v-if="item.changed" bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <span v-if="item.changed" class="item-changed"></span>
                  </v-btn>
                </template>
                {{ $t('document.table.changedTooltip') }}
              </v-tooltip>
            </template>
            <template v-slot:[`item.fileSize`]="{ item }">
              {{ item.sizeFormatted }}
            </template>
            <template v-slot:[`item.createdBy`]="{ item }">
              {{item.createdBy}}
            </template>
            <template v-slot:[`item.lastModified`]="{ item }">
              {{item.lastModifiedFormatted}}
            </template>
            <template v-slot:[`item.email`]="{ item }">
              <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on" @click="openSendDialog(item)" >
                      <v-icon>mdi-email-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('document.sendMail') }}</span>
                </v-tooltip>
            </template>
            <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
              <div class="p-table-row-actions p-visible-on-row-hover">
                <p-btn-icon type="edit" :tooltip="$t('rename')" @click="openEditDialog(item)"></p-btn-icon>
                <p-btn-icon type="delete" @click="openDeleteDialog(item)"></p-btn-icon>
              </div>
            </template>
          </v-data-table>

          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="15"
            class="mt-5"
          ></v-pagination>
        </v-col>

        <v-col v-else-if="documentsLoaded">
          <v-divider class="mb-16"></v-divider>
          <p-no-data
            :text="$t('document.noDocuments')"
            :btn-text="$t('document.createDocument')"
            img="document.png"
            :hide-btn="!hasAccess()"
            @btn-click="openAddDialog"
          ></p-no-data>
        </v-col>
      </v-row>

      <document-create-dialog
        :dialog.sync="createDialog"
      ></document-create-dialog>

      <document-edit-dialog
        :document="editedDocument"
        :dialog.sync="editDialog">
      </document-edit-dialog>

      <send-email-dialog
        :preset-attachments="presetAttachments"
        :dialog.sync="sendDialog">
      </send-email-dialog>

      <p-delete-dialog
        :dialog.sync="deleteDialog"
        :item="deletedDocument"
        :item-data="deletedDocumentData"
        :load-relations="loadRelations"
        :delete-item="deleteDocument"
        @itemDeleted="onItemDeleted"
      ></p-delete-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import moment from 'moment'

import fileHelper from '@/helpers/file.helper'

import PFileIcon from '@/components/PFileIcon'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'
import PDeleteDialog from '@/components/PDeleteDialog'

import DocumentCreateDialog from './DocumentCreateDialog'
import DocumentEditDialog from './DocumentEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import SendEmailDialog from '../../Communication/Emails/EmailSendDialog'

import eventBus from '@/eventBus'

export default {
  components: {
    PFileIcon,
    PSearchInput,
    PNoData,
    PBtnIcon,
    PDeleteDialog,
    DocumentCreateDialog,
    DocumentEditDialog,
    SendEmailDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 15,
      createDialog: false,
      editDialog: false,
      editedDocument: null,
      deleteDialog: false,
      deletedDocument: null,
      deletedDocumentData: null,
      documentsLoaded: false,
      sendDialog: false,
      presetAttachments: null
    }
  },

  computed: {
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('document', ['documents', 'documentNames']),
    ...mapGetters('changes', ['isItemChanged']),

    documentsComputed () {
      return this.documents.map(document => {
        const lastModifiedFormatted = moment(document.lastModified).fromNow()
        const sizeFormatted = document.size < 1000 ? `${document.size} B`
          : document.size < 1000000 ? `${(document.size / 1000).toFixed(2)} KB`
            : document.size < 1000000000 ? `${(document.size / 1000000).toFixed(2)} MB`
              : `${(document.size / 1000000000).toFixed(2)} GB`
        return {
          ...document,
          lastModifiedFormatted,
          sizeFormatted,
          changed: this.isItemChanged(document.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('document.table.name'), value: 'name' },
        { text: '', value: 'changed' },
        { text: this.$t('document.table.fileSize'), value: 'fileSize' },
        { text: this.$t('document.table.createdBy'), value: 'createdBy' },
        { text: this.$t('document.table.modified'), value: 'lastModified' },
        { text: this.$t('document.table.email'), value: 'email', align: 'center', sortable: false, filterable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    }
  },


  methods: {
    ...mapActions('document', ['loadDocuments', 'getDocument', 'deleteDocument']),
    ...mapActions('file', ['loadRelations']),

    async searchDocuments (val) {
      this.search = val
    },

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchDocuments(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadDocuments()
      })
    },

    setPagination (items) {
      this.pageCount = Math.ceil(items.itemsLength / this.itemsPerPage)
    },

    openAddDialog () {
      this.createDialog = true
    },

    openEditDialog (document) {
      this.editedDocument = document
      this.editDialog = true
    },

    openSendDialog (item) {
      this.presetAttachments = []
      this.presetAttachments.push(item)
      this.sendDialog = true
    },

    openDeleteDialog (document) {
      try {
        if (document) {
          this.deletedDocument = document
          this.deletedDocumentData = {
            type: this.$t('entity.document'),
            title: this.deletedDocument.name
          }
        }
        this.deleteDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    onItemDeleted () {
      this.showSnackbarSuccess(this.$t('document.documentDeleted'))
    },

    async downloadFile (document) {
      try {
        var response = await this.getDocument(document.id)
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        fileHelper.downloadFile(blob, document.name)
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },


  async created () {
    this.initEventBus()
    if (!this.currentProjectId) return false
    try {
      this.startPageLoading()
      await this.loadDocuments()
      this.documentsLoaded = true
      if (this.$router.currentRoute.params?.goto) {
        await this.searchDocuments(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
